import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Box,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  School as SchoolIcon,
  Visibility as VisibilityIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material';
import {
  getAllSystemAdmins,
  createSystemAdmin,
  updateSystemAdmin,
  deactivateSystemAdmin,
  getAssignedCourses,
  assignCoursesToAdmin,
  removeCourseAssignment,
  SystemAdminDTO,
  CreateSystemAdminRequest,
  AssignCourseRequest,
  CourseAssignmentDTO,
} from '../services/systemAdminService';
import { getAllCourses, CourseDTO } from '../services/adminCourseService';

const SystemAdminManagement: React.FC = () => {
  const [admins, setAdmins] = useState<SystemAdminDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<SystemAdminDTO | null>(null);
  const [formData, setFormData] = useState<CreateSystemAdminRequest>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    isSuperAdmin: false,
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedAdmin, setSelectedAdmin] = useState<SystemAdminDTO | null>(null);
  const [coursesDialogOpen, setCoursesDialogOpen] = useState(false);
  const [adminCourses, setAdminCourses] = useState<CourseAssignmentDTO[]>([]);
  const [assignCourseDialogOpen, setAssignCourseDialogOpen] = useState(false);
  const [availableCourses, setAvailableCourses] = useState<CourseDTO[]>([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([]);

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const data = await getAllSystemAdmins();
      setAdmins(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load system admins');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (admin?: SystemAdminDTO) => {
    if (admin) {
      setEditingAdmin(admin);
      setFormData({
        firstName: admin.firstName,
        lastName: admin.lastName,
        email: admin.email,
        password: '', // Don't pre-fill password
        isSuperAdmin: admin.isSuperAdmin,
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        isSuperAdmin: false,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAdmin(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      isSuperAdmin: false,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingAdmin) {
        await updateSystemAdmin(editingAdmin.id, formData);
      } else {
        await createSystemAdmin(formData);
      }
      handleCloseDialog();
      loadAdmins();
    } catch (err: any) {
      setError(err.message || 'Failed to save system admin');
    }
  };

  const handleDelete = async () => {
    if (!selectedAdmin) return;
    try {
      await deactivateSystemAdmin(selectedAdmin.id);
      setMenuAnchor(null);
      setSelectedAdmin(null);
      loadAdmins();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate system admin');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, admin: SystemAdminDTO) => {
    setMenuAnchor(event.currentTarget);
    setSelectedAdmin(admin);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedAdmin(null);
  };

  const handleViewCourses = async (admin: SystemAdminDTO) => {
    try {
      setSelectedAdmin(admin);
      const courses = await getAssignedCourses(admin.id);
      setAdminCourses(courses);
      setCoursesDialogOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load courses for admin');
    }
  };

  const handleAssignCourses = async (admin: SystemAdminDTO) => {
    try {
      setSelectedAdmin(admin);
      const allCourses = await getAllCourses();
      const assignedCourses = await getAssignedCourses(admin.id);
      const assignedCourseIds = assignedCourses.map(c => c.courseId);
      
      // Filter out courses already assigned
      const available = allCourses.filter(c => c.id && !assignedCourseIds.includes(c.id));
      setAvailableCourses(available);
      setSelectedCourseIds([]);
      setAssignCourseDialogOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load courses');
    }
  };

  const handleAssignCoursesSubmit = async () => {
    if (!selectedAdmin || selectedCourseIds.length === 0) return;
    
    try {
      const request: AssignCourseRequest = { courseIds: selectedCourseIds };
      await assignCoursesToAdmin(selectedAdmin.id, request);
      setAssignCourseDialogOpen(false);
      setSelectedAdmin(null);
      loadAdmins(); // Refresh admin list
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to assign courses');
    }
  };

  const handleRemoveCourse = async (courseId: number) => {
    if (!selectedAdmin) return;
    
    try {
      await removeCourseAssignment(selectedAdmin.id, courseId);
      // Refresh the courses list
      const courses = await getAssignedCourses(selectedAdmin.id);
      setAdminCourses(courses);
      loadAdmins(); // Refresh admin list to update count
    } catch (err: any) {
      setError(err.message || 'Failed to remove course from admin');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          System Admin Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Admin
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned Courses</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.map((admin) => (
              <TableRow key={admin.id}>
                <TableCell>
                  {admin.firstName} {admin.lastName}
                </TableCell>
                <TableCell>{admin.email}</TableCell>
                <TableCell>
                  <Chip
                    label={admin.isSuperAdmin ? 'Super Admin' : 'Admin'}
                    color={admin.isSuperAdmin ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={admin.status}
                    color={admin.status === 'ACTIVE' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {admin.assignedCourses?.length || 0} course(s)
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, admin)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedAdmin) handleOpenDialog(selectedAdmin);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedAdmin) handleViewCourses(selectedAdmin);
          handleMenuClose();
        }}>
          <VisibilityIcon sx={{ mr: 1 }} /> View Courses
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedAdmin) handleAssignCourses(selectedAdmin);
          handleMenuClose();
        }}>
          <AddCircleIcon sx={{ mr: 1 }} /> Assign Courses
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon sx={{ mr: 1 }} /> Deactivate
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAdmin ? 'Edit System Admin' : 'Create System Admin'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              fullWidth
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
            />
            <TextField
              label="Last Name"
              fullWidth
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
            />
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
            <TextField
              label="Password"
              fullWidth
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              helperText={editingAdmin ? 'Leave blank to keep current password' : ''}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isSuperAdmin || false}
                  onChange={(e) =>
                    setFormData({ ...formData, isSuperAdmin: e.target.checked })
                  }
                />
              }
              label="Super Admin"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAdmin ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Courses Dialog */}
      <Dialog open={coursesDialogOpen} onClose={() => setCoursesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Courses Assigned to: {selectedAdmin?.firstName} {selectedAdmin?.lastName}
        </DialogTitle>
        <DialogContent>
          {adminCourses.length === 0 ? (
            <Typography color="text.secondary">
              No courses assigned to this admin.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Course Name</TableCell>
                    <TableCell>Course Type</TableCell>
                    <TableCell>Assigned At</TableCell>
                    <TableCell>Assigned By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adminCourses.map((assignment) => (
                    <TableRow key={assignment.courseId}>
                      <TableCell>{assignment.courseName}</TableCell>
                      <TableCell>
                        <Chip label={assignment.courseType} size="small" />
                      </TableCell>
                      <TableCell>
                        {assignment.assignedAt 
                          ? new Date(assignment.assignedAt).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{assignment.assignedByName || 'System'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveCourse(assignment.courseId)}
                        >
                          <RemoveCircleIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoursesDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setCoursesDialogOpen(false);
              if (selectedAdmin) handleAssignCourses(selectedAdmin);
            }}
          >
            Assign More Courses
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Courses Dialog */}
      <Dialog open={assignCourseDialogOpen} onClose={() => setAssignCourseDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Assign Courses to: {selectedAdmin?.firstName} {selectedAdmin?.lastName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {availableCourses.length === 0 ? (
              <Typography color="text.secondary">
                All courses are already assigned to this admin.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {availableCourses.map((course) => (
                  <FormControlLabel
                    key={course.id}
                    control={
                      <Checkbox
                        checked={course.id ? selectedCourseIds.includes(course.id) : false}
                        onChange={(e) => {
                          if (course.id) {
                            if (e.target.checked) {
                              setSelectedCourseIds([...selectedCourseIds, course.id]);
                            } else {
                              setSelectedCourseIds(selectedCourseIds.filter(id => id !== course.id));
                            }
                          }
                        }}
                      />
                    }
                    label={`${course.name} (${course.courseType})`}
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignCourseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignCoursesSubmit}
            disabled={selectedCourseIds.length === 0}
          >
            Assign Selected
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SystemAdminManagement;

